/**
 * Hooks for the Checkout / Stripe domain.
 *
 * Replaces Wized requests: create_stripe_checkout, create_stripe_renewal,
 *   add_to_dashboard
 * Replaces Wized elements: checkout-button, stripe_loader, extend_button,
 *   playlist_page_extend_button, add-to-dash
 */

import { SITE_ORIGIN } from "@/lib/site";
import { useMutation } from "./useAsync";
import * as api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAppStore } from "../lib/store";

const ORIGIN =
  typeof window !== "undefined" ? window.location.origin : SITE_ORIGIN;

/** Mutation: create a Stripe checkout session and redirect. */
export function useStripeCheckout() {
  const { cartItems, setCartOpen } = useAppStore();

  return useMutation(async () => {
    const result = await api.createStripeCheckout(
      cartItems,
      `${ORIGIN}/app/dashboard/home?checkout=success`,
      `${ORIGIN}/dance-library`,
    );

    if (result.data?.url) {
      setCartOpen(false);
      window.location.href = result.data.url;
    }

    return result;
  });
}

/** Mutation: create a Stripe renewal checkout for extending a permission. */
export function useStripeRenewal() {
  return useMutation(async (permissionId: string) => {
    const result = await api.createStripeRenewal(
      permissionId,
      `${ORIGIN}/app/dashboard/home`,
      `${ORIGIN}/app/dashboard/home`,
    );

    if (result.data?.url) {
      window.location.href = result.data.url;
    }

    return result;
  });
}

/** Mutation: add a free course to the user's dashboard. */
export function useAddFreeCourse() {
  const { user } = useAuth();

  return useMutation(async (courseId: string) => {
    if (!user) return { data: null, error: { message: "Not authenticated" } };
    return api.addFreeCoursePermission(courseId);
  });
}
